import React, { useState } from 'react';
import { FormikHelpers, useFormik } from 'formik';
import { Typography, Button, TextField, Alert, Box } from '@mui/material';
import { isNil, head } from 'ramda';

import useCertificates from 'src/hooks/talents/useCertificates';
import useModals from 'src/hooks/useModals';
import {
  getValidationSchema,
  CertificateFormData,
  initialValues,
  attributesToSubmit,
} from 'src/forms/talents/certificate';
import Certificate from 'src/types/resources/CertificateType';
import {
  extractResponseErrors,
  isAxiosError,
  isUnprocessedEntityError,
  parseToFormikErrors,
} from 'src/utils/responseErrors';
import UploadFile from 'src/components/UploadFile';
import { useFeatureFlags } from 'src/hooks';
import { CERTIFICATE_VALID_FILE_TYPES, RESUME_VALID_FILE_TYPES } from 'src/utils/constants';

import useCertificatesText from '../../hooks/useCertificatesText';

import styles from './styles';

type CertificateFormProps = {
  certificate?: Certificate;
  certificateType: 'certificate' | 'resume';
  useUpdatedUI?: boolean;
  onSuccessSubmit?: (fileChanged: boolean) => void;
};

const testidPrefixDict = {
  certificate: 'talent-certificate-form',
  resume: 'talent-resume-form',
};

const CertificateForm: React.FC<CertificateFormProps> = props => {
  const { certificate, certificateType, useUpdatedUI = false, onSuccessSubmit } = props;
  const [formError, setFormError] = useState<string | null>(null);
  const { hideModal } = useModals();
  const { loadCertificates, createCertificate, updateCertificate } = useCertificates();
  const { featureEnableNewTalentOnboarding } = useFeatureFlags();

  const isResumeForm = certificateType === 'resume';
  const isNewResumeForm = isResumeForm && featureEnableNewTalentOnboarding;

  const text = useCertificatesText(certificateType);

  const isAddForm = isNil(certificate);
  const formTitle = `${isAddForm ? 'Add' : 'Edit'} ${text}`;

  const handleSubmit = async (
    values: CertificateFormData,
    { setErrors, setSubmitting }: FormikHelpers<CertificateFormData>,
  ) => {
    const params = attributesToSubmit(values);
    setFormError(null);
    try {
      const fileWasChanged = Boolean(values.file?.source);

      if (isAddForm) {
        await createCertificate({ ...params, isResume: isResumeForm }).unwrap();
      } else {
        await updateCertificate({ id: certificate.id, params }).unwrap();
      }
      loadCertificates();
      if (onSuccessSubmit) {
        onSuccessSubmit(fileWasChanged);
      }
      hideModal();
    } catch (error: unknown) {
      if (isAxiosError(error) && isUnprocessedEntityError(error)) {
        const errors = extractResponseErrors(error);
        const nonFieldErrors = error?.response?.data?.errors?.nonFieldErrors;
        if (nonFieldErrors) {
          setFormError(head(nonFieldErrors));
        }
        setErrors(parseToFormikErrors(errors));
      } else {
        setFormError('An unexpected error occurred');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleSubmit: handleFormikSubmit,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
  } = useFormik<CertificateFormData>({
    initialValues: initialValues(certificate, !isNewResumeForm),
    validationSchema: getValidationSchema(!isNewResumeForm),
    onSubmit: handleSubmit,
  });

  const handleFileUpload = (file: File | null) => {
    setFieldTouched('file');
    setFieldValue('file', {
      source: file,
      url: null,
    });
  };

  return (
    <>
      {formError && <Alert severity="error">{formError}</Alert>}
      <form noValidate onSubmit={handleFormikSubmit}>
        <Box sx={{ ...styles.box, ...(useUpdatedUI ? styles.boxUpdatedStyles : {}) }}>
          <Typography variant="h3" align="center" sx={styles.heading}>
            {formTitle}
          </Typography>
          <Box sx={styles.row}>
            <TextField
              id="documentName"
              label="Document Name"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.documentName}
              error={touched.documentName && Boolean(errors.documentName)}
              helperText={touched.documentName && errors.documentName}
            />
          </Box>
          {!isNewResumeForm && (
            <>
              <Box sx={styles.row}>
                <TextField
                  id="schoolOrUniversity"
                  label="School/University"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.schoolOrUniversity}
                  error={touched.schoolOrUniversity && Boolean(errors.schoolOrUniversity)}
                  helperText={touched.schoolOrUniversity && errors.schoolOrUniversity}
                />
              </Box>
              <Box sx={styles.row}>
                <TextField
                  id="fieldOfStudy"
                  label="Field of study"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.fieldOfStudy}
                  error={touched.fieldOfStudy && Boolean(errors.fieldOfStudy)}
                  helperText={touched.fieldOfStudy && errors.fieldOfStudy}
                />
              </Box>
            </>
          )}
          <Box sx={styles.row}>
            <UploadFile
              error={touched.file && Boolean(errors.file)}
              helperText={touched.file && (errors.file?.source || errors.file)}
              fileUrl={certificate?.file}
              onChange={handleFileUpload}
              formatsToAccept={
                isNewResumeForm ? RESUME_VALID_FILE_TYPES.join(',') : CERTIFICATE_VALID_FILE_TYPES.join(',')
              }
            />
          </Box>
          <Box sx={styles.footer}>
            <Button
              type="submit"
              variant="contained"
              sx={useUpdatedUI ? { marginRight: '10px' } : styles.button}
              disabled={isSubmitting}
              data-testid={`${testidPrefixDict[certificateType]}-save-btn`}
            >
              {isAddForm ? 'Add' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              sx={useUpdatedUI ? {} : styles.button}
              onClick={hideModal}
              data-testid={`${testidPrefixDict[certificateType]}-cancel-btn`}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default CertificateForm;
